/* =============================================================================
   hd-text.js —— 文字属性扩展（2026-09-14 批次4c）

   职责：
     · 字间距 charSpacing · 行距 lineHeight · 文字对齐 textAlign
     · **段落折行**（fabric.Textbox，按固定宽度自动换行）—— 原只有单行 IText
     · Single ↔ Paragraph 双向转换（选中文字时点切换即转换）

   边界（避免第二份实现）：
     · 本文件只负责「构造文字对象」+「应用扩展属性」+「渲染本面板控件」。
       入画布 / 选中 / 入撤销栈 / 刷新图层 仍走主 IIFE 的 addText() 同一份流程。
     · 主 IIFE 仅在 addText 里把「构造」委托过来（window.__hdText.build）。

   迁移自 hd-align.js 的两条铁律（本文件同构遵守）：
     · 事件绑定必须**可补绑**（init 由 updateLayers() 每轮调用，首次时 canvas 可能未就绪）
     · selection 事件必须**延后一拍**读状态（fabric 先 fire 事件、后赋值 _activeObject）
   ============================================================================= */
(function () {
  'use strict';

  var D = null, FROOT = null;
  var ready = false, bound = false;
  var POROOT = null;            // #pfmPathPanel（固定属性区，「文字沿路径」入口）

  var DEFAULT_LINE = 1.16;      // 与 fabric 默认一致

  function q(id) { return document.getElementById(id); }

  function CV() {
    try { if (D && D.canvas) return D.canvas() || null; } catch (e) { }
    try { return window.__pfmCanvas || null; } catch (e) { }
    return null;
  }

  function isText(o) {
    return !!o && /^(i-text|text|textbox)$/.test(String(o.type));
  }

  /* 仅「单选且为文字」时返回对象；多选（activeSelection）一律不处理 */
  function selText() {
    var c = CV(); if (!c) return null;
    var o = null;
    try { o = c.getActiveObject(); } catch (e) { }
    if (!o || o.type === 'activeSelection') return null;
    return isText(o) ? o : null;
  }

  /* ---------------------------------------------------------------------------
     扩展样式记忆（localStorage）—— 同时作为「新建文字」的初始值
     --------------------------------------------------------------------------- */
  var LS = 'pfmTextExt';

  function loadX() {
    try {
      var o = JSON.parse(localStorage.getItem(LS) || '{}') || {};
      return {
        spacing: (typeof o.spacing === 'number' && isFinite(o.spacing)) ? o.spacing : 0,
        line: (typeof o.line === 'number' && o.line > 0.2 && o.line < 6) ? o.line : DEFAULT_LINE,
        align: (o.align === 'left' || o.align === 'right') ? o.align : 'center',
        wrap: !!o.wrap,
        /* 2026-10-14: 弧度 = 圆弧扫角（度）。默认 52° ≈ 旧默认观感；360° = 首尾闭合成整圆 */
        curveDeg: (typeof o.curveDeg === 'number' && o.curveDeg >= 0 && o.curveDeg <= 360) ? o.curveDeg : 52,
        /* 2026-09-16: 文字阴影预设（none/soft/hard/glow）—— 与 spacing/line/align 同一份状态 */
        shadow: SHADOW_KINDS.indexOf(o.shadow) >= 0 ? o.shadow : 'none',
        /* 2026-10-24（多选阴影）：shadows = 选中档位数组（多选真源）。旧单值 shadow 兼容映射。 */
        shadows: (Array.isArray(o.shadows) ? o.shadows : (o.shadow && o.shadow !== 'none' ? [o.shadow] : []))
          .filter(function (k) { return SHADOW_KINDS.indexOf(k) >= 0 && k !== 'none'; }),
        /* 2026-09-20: 手调参数（优先于档位预设）—— 只接受结构完整的对象 */
        shadowOverride: (o.shadowOverride && typeof o.shadowOverride === 'object'
          && typeof o.shadowOverride.color === 'string'
          && isFinite(o.shadowOverride.blur) && isFinite(o.shadowOverride.offsetX) && isFinite(o.shadowOverride.offsetY))
          ? { color: o.shadowOverride.color, blur: o.shadowOverride.blur, offsetX: o.shadowOverride.offsetX, offsetY: o.shadowOverride.offsetY }
          : null
      };
    } catch (e) {
      return { spacing: 0, line: DEFAULT_LINE, align: 'center', wrap: false, shadow: 'none', shadowOverride: null, curveDeg: 52 };
    }
  }
  function saveX(x) { try { localStorage.setItem(LS, JSON.stringify(x)); } catch (e) { } }

  /* ---------------------------------------------------------------------------
     构造文字对象（主 IIFE 的 addText 调用；**不含位置** —— 落位由主 IIFE 负责）
     --------------------------------------------------------------------------- */
  function build(txt, opts) {
    opts = opts || {};
    var x = loadX();
    var c = CV();
    var pa = c && c._pfmPrintArea;
    var common = {
      fontSize: opts.size || 36,
      fontFamily: opts.font || 'Arial',
      fill: opts.color || '#000',
      stroke: opts.outlineC || '#fff',
      strokeWidth: opts.outlineW || 0,
      charSpacing: x.spacing,
      lineHeight: x.line,
      textAlign: x.align
    };
    var _inst;
    if (x.wrap) {
      /* 段落宽度取打印区的 70% —— 留出左右余量，用户可再拖控制点调整 */
      var w = (pa && pa.width > 0) ? Math.round(pa.width * 0.7) : 600;
      _inst = new fabric.Textbox(txt || 'Your Text', Object.assign({ width: w }, common));
    } else {
      _inst = new fabric.IText(txt || 'Your Text', common);
    }
    /* 2026-10-14: 新建文字带上当前弧度（度）；applyArcToText 读 t.pfmArcDeg */
    try { _inst.pfmArcDeg = x.curveDeg; } catch (e) { }
    /* 2026-10-24（多选阴影）：新建即装多轮渲染包装（幂等）+ 带上记忆的多选档 */
    try { _inst._pfmShadows = (x.shadows && x.shadows.length) ? x.shadows.slice() : []; installMultiShadow(_inst); } catch (e) { }
    return _inst;
  }

  /* ---------------------------------------------------------------------------
     Single ↔ Paragraph 转换
     —— 属性逐个搬运；path 相关必须一并带走，否则弧形文字会掉回直线
     --------------------------------------------------------------------------- */
  function convert(t, wantWrap) {
    if (!t) return null;
    var isBox = (t.type === 'textbox');
    if (!!wantWrap === isBox) return t;
    var c = CV(); if (!c) return t;

    var props = {
      left: t.left, top: t.top, angle: t.angle || 0,
      originX: t.originX, originY: t.originY,
      scaleX: t.scaleX, scaleY: t.scaleY,
      fontSize: t.fontSize, fontFamily: t.fontFamily,
      fontWeight: t.fontWeight, fontStyle: t.fontStyle,
      fill: t.fill, stroke: t.stroke, strokeWidth: t.strokeWidth,
      charSpacing: t.charSpacing, lineHeight: t.lineHeight, textAlign: t.textAlign,
      opacity: t.opacity,
      path: t.path || undefined,
      pathSide: t.pathSide || undefined,
      pathAlign: t.pathAlign || undefined,
      pathStartOffset: typeof t.pathStartOffset === 'number' ? t.pathStartOffset : undefined,
      pfmName: t.pfmName || 'Text'
    };
    var nt;
    if (wantWrap) {
      props.width = (t.width && t.width > 20) ? t.width : 400;
      nt = new fabric.Textbox(t.text || '', props);
    } else {
      nt = new fabric.IText(t.text || '', props);
    }
    try { c.remove(t); } catch (e) { }
    try { c.add(nt); } catch (e) { }
    try { c.setActiveObject(nt); } catch (e) { }
    try { c.renderAll(); } catch (e) { }
    try { if (D && D.save) D.save(); } catch (e) { }
    try { if (window.__pfmUpdateLayers) window.__pfmUpdateLayers(); } catch (e) { }
    return nt;
  }

  /* ---------------------------------------------------------------------------
     应用扩展属性到选中文字
     --------------------------------------------------------------------------- */
  function applyExtras() {
    var t = selText(); if (!t) return;
    var x = loadX();
    try {
      t.set({ charSpacing: x.spacing, lineHeight: x.line, textAlign: x.align });
      /* 2026-10-24（多选阴影）：shadows 数组 = 多选真源；shadow 仍存单值（最后一个/手调）供 fabric 单值兼容。 */
      var _shArr = (x.shadows && x.shadows.length) ? x.shadows.slice() : [];
      t._pfmShadows = _shArr;
      t.set({ shadow: shadowForX(t, x) });   /* 手调优先；null = 清除阴影 */
      installMultiShadow(t);
      if (t.setCoords) t.setCoords();
    } catch (e) { }
    /* 🔴 字间距会改变文字总宽 → 弧形文字的路径长度是按文字宽度算的，
       不重算会让文字溢出曲线（applyArcToText 是主 IIFE 原语，此处只能通过桥接调用） */
    try {
      if (t.path && window.__pfmApplyArcToText) {
        var arcEl = q('pfmTextArc');
        window.__pfmApplyArcToText(t, arcEl ? arcEl.value : 'up');
      }
    } catch (e) { }
    var c = CV();
    try { if (c) c.renderAll(); } catch (e) { }
    try { if (D && D.save) D.save(); } catch (e) { }
    refreshDisabled();   /* 字间距会改变折行数 ⇒ 重算 LINE/ALIGN 是否有效 */
  }

  /* 文字面板手调：把控件值存成 override（优先于档位预设）并立即套到选中文字上。
     刻意**不**走 applyExtras —— 那会再按档位预设覆盖一次手调值。 */
  function applyTextShadow() {
    var UI = window.__pfmShadowUI; if (!UI) return;
    var ov = UI.read('TxSh');
    var x = loadX(); x.shadowOverride = ov; saveX(x);
    var t = selText();
    if (t) {
      try { t.set({ shadow: shadowFor(t, null, ov) }); if (t.setCoords) t.setCoords(); } catch (e) { }
      var c = CV(); try { if (c) c.renderAll(); } catch (e) { }
      try { if (D && D.save) D.save(); } catch (e) { }
    }
    syncText();
  }

  /* ---------------------------------------------------------------------------
     2026-10-14：弧度模型由「二次贝塞尔深 U」改为「圆弧扫角」。
     滑块 = 扫角 θ(度)；半径 r = 文字宽 / θ ⇒ 弧长恒等于文字宽
     ⇒ θ=360° 时圆周恰等于文字宽，文字**首尾严丝合缝闭合成整圆**。
     路径只用 M/C 命令（圆弧按 ≤90° 分段做三次贝塞尔逼近，K=4/3·tan(Δ/4)）。
     --------------------------------------------------------------------------- */
  var CURVE_DEF = 52;

  function fx(v) { return Math.round(v * 100) / 100; }

  function curveDeg() {
    var v = loadX().curveDeg;
    return (typeof v === 'number' && isFinite(v)) ? v : CURVE_DEF;
  }

  /* 生成弧形路径；返回 fabric.Path 或 null（不弧） */
  function arcPath(t, arc) {
    if (!t || !arc || arc === 'none') return null;
    var deg = (typeof t.pfmArcDeg === 'number' && isFinite(t.pfmArcDeg)) ? t.pfmArcDeg : curveDeg();
    if (!(deg > 0)) return null;
    if (deg > 360) deg = 360;
    if (deg < 3) deg = 3;                        /* 太小半径爆炸 → 给下限 */
    var size = t.fontSize || 100, fname = t.fontFamily || 'Arial';
    var ch = (typeof t.charSpacing === 'number' && isFinite(t.charSpacing)) ? t.charSpacing : 0;
    /* 🔴 2026-10-14 修正：路径长必须等于 **fabric 居中时真正用的 a**，否则 fabric 的 _measureLine
       会执行 `o < l ? l %= o : l < 0 && (l += o)` —— 把字符**回绕**到路径另一端 ⇒ 字符乱序。
       fabric 的 getLineWidth → measureLine 会 `width -= fontSize*charSpacing/1000`
       ⇒ **a = probe.width + chPx**。间距=0 时两者相等（所以只有调间距才暴露）。 */
    var chPx = (ch !== 0) ? (size * ch / 1000) : 0;
    /* 探针必须带**实际字间距**；IText = 不折行 = 多行时各行的安全上界 */
    var probe = new fabric.IText(t.text || 'Your Text', {
      fontSize: size, fontFamily: fname,
      fontWeight: t.fontWeight || 'normal', fontStyle: t.fontStyle || 'normal',
      charSpacing: ch, fill: '#000'
    });
    var want = (probe.width || size * 2) + chPx;   /* = fabric 的 a */
    if (probe.destroy) probe.destroy();
    var th = deg * Math.PI / 180;
    var dir = (arc === 'down') ? -1 : 1;
    /* 目标弧长 = a + 极小安全余量（0.1% + 1px）⇒ 保证 fabric 量到的 o ≥ a，绝不回绕。
       误差预算：三次贝塞尔逼近圆 ≈0.02% + fabric 折线采样 ≈0.003%，合计 ≪ 0.1%。
       代价：360° 时收口缝隙 ≈1.5px（0.1% 周长），肉眼不可见。 */
    var target = want * 1.001 + 1;
    var r = target / th;                         /* 弧长 ≈ 文字宽（360° 时闭合） */
    var sag = r * (1 - Math.cos(th / 2));        /* 矢高 */
    try { t.pfmArcSag = Math.abs(sag); } catch (e) { }   /* 2026-10-24: 记矢高供包围盒修正用 */
    var n = Math.max(1, Math.ceil(deg / 90));    /* 每段 ≤ 90° */
    var step = th / n, k = (4 / 3) * Math.tan(step / 4) * r;
    var a = -th / 2;
    var Px = function (v2) { return r * Math.sin(v2); };
    var Py = function (v2) { return dir * (r - r * Math.cos(v2)) - dir * sag / 2; };
    var Tx = function (v2) { return Math.cos(v2); };
    var Ty = function (v2) { return dir * Math.sin(v2); };
    var d = 'M ' + fx(Px(a)) + ',' + fx(Py(a));
    for (var i = 0; i < n; i++) {
      var b = a + step;
      d += ' C ' + fx(Px(a) + k * Tx(a)) + ',' + fx(Py(a) + k * Ty(a))
        + ' ' + fx(Px(b) - k * Tx(b)) + ',' + fx(Py(b) - k * Ty(b))
        + ' ' + fx(Px(b)) + ',' + fx(Py(b));
      a = b;
    }
    try { t.pfmArcDir = (arc === 'down') ? 'down' : 'up'; } catch (e) { }
    return new fabric.Path(d, { fill: '', stroke: '', selectable: false, evented: false });
  }

  /* 滑块回调：写对象级弧度并立即重算弧线（ARC = none 时只记值、暂不画弧） */
  function applyCurve(deg) {
    var t = selText(); if (!t) return;
    var d = Math.max(0, Math.min(360, deg));
    t.pfmArcDeg = d;
    try {
      if (t.path && window.__pfmApplyArcToText) {
        var arcEl = q('pfmTextArc');
        var av = t.pfmArcDir || (arcEl ? arcEl.value : 'up');
        if (av === 'none') av = 'up';
        window.__pfmApplyArcToText(t, av);
      }
    } catch (e) { }
    var c = CV(); try { if (c) c.renderAll(); } catch (e) { }
    try { if (D && D.save) D.save(); } catch (e) { }
  }

  /* ---------------------------------------------------------------------------
     2026-10-14：文本框 ↔ 选中文字 **双向同步**（原先两边完全没连线）
       input  → 逐键改选中文字（含弧形重算）+ 立即重绘
       change → 失焦时才入一次撤销栈（避免每敲一个字一条）
       text:changed（画布双击改字）→ 走 syncText 回填文本框
     --------------------------------------------------------------------------- */
  function syncInputToText(commit) {
    var t = selText(); if (!t) return;
    var el = q('pfmTextInput'); if (!el) return;
    try { t.set({ text: el.value }); } catch (e) { return; }
    /* 文字宽变了 ⇒ 弧形路径半径必须重算，否则弧形文字会溢出曲线 */
    try {
      if (t.path && window.__pfmApplyArcToText) {
        var arcEl = q('pfmTextArc');
        window.__pfmApplyArcToText(t, t.pfmArcDir || (arcEl ? arcEl.value : 'up'));
      }
    } catch (e) { }
    try { if (t.setCoords) t.setCoords(); } catch (e) { }
    var c = CV(); try { if (c) c.renderAll(); } catch (e) { }
    refreshDisabled();   /* 字数/折行变了 ⇒ 重算哪些控件无效 */
    if (commit) { try { if (D && D.save) D.save(); } catch (e) { } }
  }

  /* ---------------------------------------------------------------------------
     2026-10-14：「此刻无效」的控件置灰（.pfm-row--off）——
       CURVE 只在 ARC = Arc Up / Arc Down 时有效
       LINE  只在 ≥2 行时有效
       ALIGN 只在 ≥2 行、或定宽框（Textbox）时有效
     ⚠️ 无选中文字时**不灰** —— 这些值同时是新建文字的默认值。
     --------------------------------------------------------------------------- */
  function effLineCount(t) {
    if (!t) return 1;
    try { if (t._textLines && t._textLines.length) return t._textLines.length; } catch (e) { }
    return String(t.text || '').split('\n').length;
  }

  function setRowOff(id, off) {
    var row = q(id); if (!row) return;
    off = !!off;
    row.classList.toggle('pfm-row--off', off);
    row.setAttribute('aria-disabled', off ? 'true' : 'false');
    var cs = row.querySelectorAll('input,button');
    for (var i = 0; i < cs.length; i++) cs[i].disabled = off;
  }

  function refreshDisabled() {
    var t = selText();
    var arcEl = q('pfmTextArc');
    var av = arcEl ? arcEl.value : 'none';
    var multi = t ? (effLineCount(t) > 1) : true;              /* 无选中 ⇒ 视作多行，保持可用 */
    var fixedW = !!(t && String(t.type) === 'textbox');
    setRowOff('pfmTxCurveRow', !(av === 'up' || av === 'down'));
    setRowOff('pfmTxLineRow', !multi);
    setRowOff('pfmTxAlignRow', !(multi || fixedW));
  }

  /* ARC 下拉变化后要重算 CURVE 的可用性（design-canvas 的 change 监听先跑，本监听随后） */
  function bindArcWatch() {
    var sel = q('pfmTextArc');
    if (!sel || sel.dataset.hdArcBound === '1') return;
    sel.dataset.hdArcBound = '1';
    sel.addEventListener('change', function () { refreshDisabled(); });
  }

  function bindInput() {
    var el = q('pfmTextInput');
    if (!el || el.dataset.hdTextBound === '1') return;   /* 幂等：可重复调 */
    el.dataset.hdTextBound = '1';
    el.addEventListener('input', function () { syncInputToText(false); });
    el.addEventListener('change', function () { syncInputToText(true); });
  }

  function toast(m) {
    try {
      if (D && D.toast) { D.toast(m); return; }
      if (window.__pfmToast) { window.__pfmToast(m); }
    } catch (e) { }
  }

  /* ---------------------------------------------------------------------------
     文字沿任意路径（批次4d）

     ⚠️ 关键事实（PoC 实测）：fabric 的 `text.path` **不会应用 path 自身 transform**，
        且路径坐标是**文字的 local 坐标**。因此：
          ① 必须用「已把 scale/angle 烘焙进路径点」的画布坐标路径
             → 复用 window.__hdVector.pathDataOf()（内部走 objToPaper）
          ② 再整体平移「以形状中心为原点」，文字锚点落在形状中心即对齐
     --------------------------------------------------------------------------- */
  var PATHABLE = { rect: 1, ellipse: 1, circle: 1, triangle: 1, polygon: 1, line: 1, path: 1 };

  function selShape() {
    var c = CV(); if (!c) return null;
    var o = null;
    try { o = c.getActiveObject(); } catch (e) { }
    if (!o || o.type === 'activeSelection') return null;
    if (o.isGrid || o.isPreview) return null;
    if (isText(o) || o.type === 'image') return null;
    return PATHABLE[String(o.type)] ? o : null;
  }

  function shapeLabel(o) {
    if (!o) return '';
    if (o.pfmName) return String(o.pfmName);
    var k = o.pfmShapeKind || o.type || '';
    return k.charAt(0).toUpperCase() + k.slice(1);
  }

  /* 把 path d 整体平移 —— 用 fabric 自己解析，避免手写正则漏掉 e 记数法等写法 */
  function shiftPathD(d, dx, dy) {
    try {
      var p = new fabric.Path(d);
      var arr = p.path || [];
      if (!arr.length) return null;
      arr.forEach(function (seg) {
        for (var i = 1; i < seg.length - 1; i += 2) {
          if (typeof seg[i] === 'number' && typeof seg[i + 1] === 'number') {
            seg[i] = seg[i] + dx;
            seg[i + 1] = seg[i + 1] + dy;
          }
        }
      });
      return arr.map(function (seg) { return seg.join(' '); }).join(' ');
    } catch (e) { return null; }
  }

  function createOnPath() {
    var shape = selShape();
    if (!shape) { toast('Select a shape first'); return; }
    var V = window.__hdVector;
    if (!V || !V.pathDataOf) { toast('Shape engine not ready'); return; }
    toast('Building path…');

    V.pathDataOf(shape).then(function (d) {
      try {
        if (!d) { toast('This shape cannot be used as a path'); return; }
        var ctr = (shape.getCenterPoint ? shape.getCenterPoint() : null) || { x: 0, y: 0 };
        var local = shiftPathD(d, -ctr.x, -ctr.y);
        if (!local) { toast('Path build failed'); return; }

        var x = loadX();
        var ta = q('pfmTextInput');
        var txt = (ta && ta.value) ? ta.value : 'Your Text';
        var sizeEl = q('pfmFontSize');
        var colEl = q('pfmTextColor');
        var fontEl = q('pfmFontSelect');
        var size = sizeEl ? (parseInt(sizeEl.value, 10) || 36) : 36;
        /* 2026-10-14（B）：#pfmTextColor 已从面板移除 ⇒ 兜底取「当前对象 fill」，
           否则新建的路径文字会一律是黑。 */
        var color = (colEl && colEl.value) ? colEl.value
          : (function(){ try{ var _o=window.__pfmCanvas&&window.__pfmCanvas.getActiveObject();
                             var _f=_o&&_o.fill; if(typeof _f==='string'&&_f) return _f; }catch(e){} return '#000000'; })();
        var fam = (fontEl && fontEl.value) ? fontEl.value : 'Arial';

        var path = new fabric.Path(local, {
          left: 0, top: 0, fill: '', stroke: '', selectable: false, evented: false
        });
        var t = new fabric.IText(txt, {
          fontSize: size, fontFamily: fam, fill: color,
          left: ctr.x, top: ctr.y, originX: 'center', originY: 'center',
          charSpacing: x.spacing, lineHeight: x.line, textAlign: x.align,
          path: path, pathSide: 'center', pathAlign: 'center', pathStartOffset: 0
        });
        t.pfmName = 'Path Text';
        t.pfmPathKind = 'custom';   /* 2026-10-14: 自定义形状路径 —— ARC 设置不得覆盖它 */

        if (window.__pfmAddObject) window.__pfmAddObject(t);
        else {
          var c = CV();
          if (c) { c.add(t); c.setActiveObject(t); c.renderAll(); }
        }
        toast('Text placed on path');
        try { syncText(); syncPath(); } catch (e) { }
      } catch (e) {
        toast('Path text failed: ' + ((e && e.message) || e));
      }
    }).catch(function (e) {
      toast('Path failed: ' + ((e && e.message) || e));
    });
  }

  /* ---------------------------------------------------------------------------
     面板控件
     --------------------------------------------------------------------------- */  function alignSvg(k) {
    var s = '<svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true" focusable="false">';
    var bar = function (x, y, w) {
      return '<rect x="' + x + '" y="' + y + '" width="' + w + '" height="1.8" rx="0.9" fill="currentColor"/>';
    };
    if (k === 'left') { s += bar(2, 3.5, 12) + bar(2, 7.1, 8) + bar(2, 10.7, 10.5); }
    else if (k === 'center') { s += bar(2, 3.5, 12) + bar(4, 7.1, 8) + bar(2.75, 10.7, 10.5); }
    else { s += bar(2, 3.5, 12) + bar(6, 7.1, 8) + bar(3.5, 10.7, 10.5); }
    return s + '</svg>';
  }

  /* ===========================================================================
     文字阴影 / 发光（2026-09-16）
     ---------------------------------------------------------------------------
     预设 4 档（唯一实现，禁第二份）：
       none  无
       soft  柔和投影  rgba(0,0,0,.35) blur 8  offsetY 4
       hard  硬边投影  rgba(0,0,0,.85) blur 0  offsetY 6
       glow  同色光晕  = 文字自身填充色，blur 22 offsetY 0
     反向识别用于「选中对象时高亮对应档」（blur 0→hard / blur≥16→glow / 其余→soft）。
     =========================================================================== */
  var SHADOW_KINDS = ['none', 'soft', 'hard', 'glow', 'long', 'neon', 'lift', 'sunken',
                      'cast', 'retro', 'extrude', 'deep', 'fog', 'halo', 'underlight', 'motion'];
  /* 2026-09-20: 面板文案（引擎参数只认 kind，参数唯一实现在本文件的 shadowFor） */
  var SHADOW_LABELS = { none: 'None', soft: 'Soft', hard: 'Hard', glow: 'Glow', long: 'Long',
    neon: 'Neon', lift: 'Lift', sunken: 'Sunken', cast: 'Cast', retro: 'Retro', extrude: 'Extrude',
    deep: 'Deep', fog: 'Fog', halo: 'Halo', underlight: 'Underlight', motion: 'Motion' };

  /* 🔴 参数按**字号等比缩放**（2026-09-16 实测教训）：
     写死 blur 8 / offsetY 4 时，150px 字号在 3000² 画布上缩到屏幕只有 1~2px，
     用户看到的就是「点了没反应」。字号跨度 8~500 ⇒ 跟字号走 + 上下限防极端。
     soft  blur 10%·offY 6%   hard  blur 0·offY 7%   glow  blur 22%·同色          */
  function shadowScale(fs, pct, lo, hi) {
    return Math.max(lo, Math.min(hi, Math.round(fs * pct)));
  }

  /* 尺寸基准：文字用字号；其它对象用宽高的几何平均（宽扁对象用 min 会过小、max 会过大）。
     夹在 [16,400] 防极端。批次 B 的「对象投影」与文字共用这一套参数。 */
  function sizeBasis(o) {
    if (!o) return 150;
    if (String(o.type || '').toLowerCase().indexOf('text') >= 0) return o.fontSize || 150;
    try {
      var w = o.getScaledWidth(), h = o.getScaledHeight();
      if (!(w > 0) || !(h > 0)) return 150;
      return Math.max(16, Math.min(400, Math.sqrt(w * h)));
    } catch (e) { return 150; }
  }

  /* ---------- 阴影颜色解析 / 合成（UI 回填用；不依赖 canvas） ---------- */
  function shColorParts(c) {
    var hex = '#000000', a = 1, m;
    if (typeof c === 'string' && c) {
      m = c.match(/rgba?\(([^)]+)\)/i);
      if (m) {
        var p = m[1].split(',');
        hex = '#' + [0, 1, 2].map(function (k) {
          var h = Math.max(0, Math.min(255, Math.round(parseFloat(p[k]) || 0))).toString(16);
          return h.length < 2 ? '0' + h : h;
        }).join('');
        if (p.length > 3) a = parseFloat(p[3]);
      } else if (c.charAt(0) === '#') {
        hex = c.length === 4 ? '#' + c[1] + c[1] + c[2] + c[2] + c[3] + c[3] : c.slice(0, 7);
      }
    }
    return { hex: hex, alpha: Math.round((isFinite(a) ? a : 1) * 100) };
  }
  function shRgba(hex, pct) {
    var h = String(hex || '#000').replace('#', '');
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    var n = parseInt(h, 16); if (!isFinite(n)) n = 0;
    var a = Math.max(0, Math.min(100, parseInt(pct, 10) || 0)) / 100;
    return 'rgba(' + ((n >> 16) & 255) + ',' + ((n >> 8) & 255) + ',' + (n & 255) + ',' + a.toFixed(2) + ')';
  }

  /* 第三个参数是「用户手调」：给了就用**绝对值**（所见即所得），不再按尺寸等比 */
  function shadowFor(t, kind, ov) {
    var F = window.fabric;
    if (!F || !F.Shadow) return null;
    if (ov) {
      return new F.Shadow({
        color: ov.color || 'rgba(0,0,0,0.35)',
        blur: Math.max(0, Math.round(ov.blur || 0)),
        offsetX: Math.round(ov.offsetX || 0),
        offsetY: Math.round(ov.offsetY || 0)
      });
    }
    if (!kind || kind === 'none') return null;
    var fs = sizeBasis(t);
    /* 2026-09-20: 「对象自身颜色」——图片对象的 fill 恒为 rgb(0,0,0)，照搬会得到**黑色**，
       故图片一律用站内主色。（原 glow 分支里的局部实现上提到这里，供 glow 与下面几个
       tint 类档位共用，不写第二份。）shRgba 只吃 hex ⇒ 非 hex 的 fill 回落到主色。 */
    var _fill = (String(t.type || '').toLowerCase().indexOf('image') < 0 && typeof t.fill === 'string' && t.fill) ? t.fill : '#7C3AED';
    var objTint = (_fill.charAt(0) === '#') ? _fill : '#7C3AED';
    if (kind === 'hard') {
      return new F.Shadow({ color: 'rgba(0,0,0,0.85)', blur: 0, offsetX: 0, offsetY: shadowScale(fs, 0.07, 3, 70) });
    }
    if (kind === 'glow') {
      return new F.Shadow({ color: _fill, blur: shadowScale(fs, 0.22, 8, 160), offsetX: 0, offsetY: 0 });
    }
    if (kind === 'long') {
      return new F.Shadow({ color: 'rgba(0,0,0,0.45)', blur: 0, offsetX: 0, offsetY: shadowScale(fs, 0.20, 6, 90) });
    }
    if (kind === 'neon') {
      return new F.Shadow({ color: 'rgba(124,58,237,0.95)', blur: shadowScale(fs, 0.30, 10, 170), offsetX: 0, offsetY: 0 });
    }
    if (kind === 'lift') {
      return new F.Shadow({ color: 'rgba(0,0,0,0.30)', blur: shadowScale(fs, 0.28, 8, 150), offsetX: 0, offsetY: shadowScale(fs, 0.14, 4, 90) });
    }
    if (kind === 'sunken') {
      return new F.Shadow({ color: 'rgba(0,0,0,0.45)', blur: shadowScale(fs, 0.12, 4, 100), offsetX: 0, offsetY: -shadowScale(fs, 0.10, 3, 80) });
    }
    /* 2026-09-20（用户要求「shadow 加8种」）—— 全部是**单层** fabric.Shadow 的参数变体；
       参数与既有 7 档在 blur / offsetX / offsetY 三轴上均留 ≥0.08×basis 间距，
       配合 shadowKind() 新增的 offsetX 比对 ⇒ 反查（胶囊高亮）不会串档。 */
    if (kind === 'cast') {                 /* 45° 硬投影 */
      var cst = shadowScale(fs, 0.10, 3, 90);
      return new F.Shadow({ color: 'rgba(0,0,0,0.80)', blur: 0, offsetX: cst, offsetY: cst });
    }
    if (kind === 'retro') {                /* 错版印刷（对象色错位） */
      var rtr = shadowScale(fs, 0.13, 4, 110);
      return new F.Shadow({ color: shRgba(objTint, 100), blur: 0, offsetX: -rtr, offsetY: rtr });
    }
    if (kind === 'extrude') {              /* 3D 挤出（厚实） */
      return new F.Shadow({ color: 'rgba(0,0,0,0.55)', blur: 0, offsetX: 0, offsetY: shadowScale(fs, 0.30, 8, 150) });
    }
    if (kind === 'deep') {                 /* 高空悬浮（大位移 + 大模糊） */
      return new F.Shadow({ color: 'rgba(0,0,0,0.32)', blur: shadowScale(fs, 0.38, 12, 200), offsetX: 0, offsetY: shadowScale(fs, 0.26, 6, 140) });
    }
    if (kind === 'fog') {                  /* 雾团（无方向弥散） */
      return new F.Shadow({ color: 'rgba(0,0,0,0.26)', blur: shadowScale(fs, 0.50, 16, 240), offsetX: 0, offsetY: 0 });
    }
    if (kind === 'halo') {                 /* 光环（对象色，比 Glow 更大） */
      return new F.Shadow({ color: shRgba(objTint, 90), blur: shadowScale(fs, 0.40, 14, 220), offsetX: 0, offsetY: 0 });
    }
    if (kind === 'underlight') {           /* 底部打光（光从下方来） */
      return new F.Shadow({ color: shRgba(objTint, 85), blur: shadowScale(fs, 0.24, 8, 160), offsetX: 0, offsetY: -shadowScale(fs, 0.20, 6, 110) });
    }
    if (kind === 'motion') {               /* 水平拖影（运动感） */
      return new F.Shadow({ color: 'rgba(0,0,0,0.30)', blur: shadowScale(fs, 0.16, 5, 120), offsetX: shadowScale(fs, 0.26, 6, 150), offsetY: 0 });
    }
    return new F.Shadow({ color: 'rgba(0,0,0,0.35)', blur: shadowScale(fs, 0.10, 4, 90), offsetX: 0, offsetY: shadowScale(fs, 0.06, 3, 60) });
  }

  /* 预设的等效参数（含颜色）—— UI 回填滑杆、以及反查高亮都用它，免得写第二份数字 */
  function shadowPreset(t, kind) {
    var s = (kind && kind !== 'none') ? shadowFor(t, kind) : null;
    if (!s) return null;
    var c = shColorParts(s.color);
    return { hex: c.hex, alpha: c.alpha, blur: Math.round(s.blur || 0), offsetX: Math.round(s.offsetX || 0), offsetY: Math.round(s.offsetY || 0) };
  }

  var SHADOW_PRESETS = ['soft', 'hard', 'glow', 'long', 'neon', 'lift', 'sunken',
                         'cast', 'retro', 'extrude', 'deep', 'fog', 'halo', 'underlight', 'motion'];

  /* 反查：按各预设的等效参数逐一比对（容差随尺寸）—— 都不匹配就是用户手调过的 */
  function shadowKind(sh, t) {
    if (!sh) return 'none';
    var fs = sizeBasis(t), tol = Math.max(1.5, fs * 0.02);
    for (var i = 0; i < SHADOW_PRESETS.length; i++) {
      var p = shadowPreset(t, SHADOW_PRESETS[i]);
      if (!p) continue;
      /* 2026-09-20: 补 offsetX —— 只比 blur/offsetY 时，靠水平位移区分的档位
         （cast / retro / motion）会被认成别的档 ⇒ 胶囊高亮错。 */
      if (Math.abs((sh.blur || 0) - p.blur) <= tol &&
          Math.abs((sh.offsetX || 0) - (p.offsetX || 0)) <= tol &&
          Math.abs((sh.offsetY || 0) - p.offsetY) <= tol) return SHADOW_PRESETS[i];
    }
    return 'custom';
  }

  /* 文字对象该套哪个阴影：**手调优先**（shadowOverride），否则用档位预设 —— 判定只此一处 */
  function shadowForX(t, x) {
    if (x && x.shadowOverride) return shadowFor(t, null, x.shadowOverride);
    return shadowFor(t, (x && x.shadow) || 'none');
  }

  /* 2026-10-24（多选阴影）：fabric 每对象只认一个 shadow ⇒ 多选走「多轮渲染叠加」。
     _pfmShadows = 选中档位数组（>1 才多轮）；每轮设一个档位的 shadow 完整画一遍（文字本体不透明，
     重复画同位置同色无视觉差，各档阴影自然叠）。render 是运行时函数不进快照 ⇒ 恢复后由
     hd-canvas.js 的 object:added 钩子重装（幂等 _pfmShHook）。 */
  function installMultiShadow(o) {
    try {
      if (!o || !window.fabric) return;
      var ty = String(o.type || '');
      if (ty !== 'text' && ty !== 'i-text' && ty !== 'textbox') return;
      if (o._pfmShHook) return;
      o._pfmShHook = true;
      var orig = o.render;
      o.render = function (ctx) {
        var kinds = this._pfmShadows;
        if (!kinds || kinds.length <= 1) { return orig.call(this, ctx); }
        var saved = this.shadow;
        for (var i = 0; i < kinds.length; i++) {
          this.shadow = shadowFor(this, kinds[i]);
          try { orig.call(this, ctx); } catch (e) { }
        }
        this.shadow = saved;
      };
    } catch (e) { }
  }
  try { window.__pfmInstallMultiShadow = installMultiShadow; } catch (e) { }

  /* 供固定属性区的「对象投影」行（hd-edit.js）复用 —— 同一套数学，不写第二份实现 */
  try {
    window.__pfmShadow = {
      for: shadowFor, kindOf: shadowKind, basis: sizeBasis,
      preset: shadowPreset, color: shColorParts, rgba: shRgba, kinds: SHADOW_PRESETS
    };
  } catch (e) { }

  /* ============ 阴影「手调控件」—— 公共片段（对象组 Shadow 面板 + Text 面板共用一份）============
     2026-09-20（用户要求 1/2/3）：
       · 范围与引擎 clamp 对齐：Blur 上限 240（原来只有 160，fog=240/deep=200 回填会被浏览器夹断）；
       · **Distance + Angle** 表达任意方向。Angle 约定 0°=向下、顺时针为正 ⇒
         offsetX = r·sinθ、offsetY = r·cosθ，与既有档位一致
         （soft/hard/long=0° · cast=45° · motion=90° · sunken/underlight=180° · retro=315°）。
       · id 前缀区分实例：对象组 'Sh'（沿用旧 id pfmShColor…）、Text 面板 'TxSh'
         —— 两个面板会同时存在于 DOM，同 id 会让 getElementById 拿错元素。
     ============================================================================ */
  function shUiIds(pf) {
    return { color: 'pfm' + pf + 'Color', alpha: 'pfm' + pf + 'Alpha', dist: 'pfm' + pf + 'Dist',
             angle: 'pfm' + pf + 'Angle', blur: 'pfm' + pf + 'Blur' };
  }
  function shUiHtml(pf) {
    var i = shUiIds(pf);
    return ''
      + '<div class="pfm-xf-row">'
      +   '<label class="pfm-xf-lab" for="' + i.color + '">Colour</label>'
      +   '<input type="color" class="pfm-sh-color" id="' + i.color + '" aria-label="Shadow colour">'
      +   '<label class="pfm-xf-lab" for="' + i.alpha + '">Opacity</label>'
      +   '<input type="range" class="pfm-bb-range" id="' + i.alpha + '" min="0" max="100" step="1" aria-label="Shadow opacity">'
      + '</div>'
      + '<div class="pfm-xf-row">'
      +   '<label class="pfm-xf-lab" for="' + i.dist + '">Distance</label>'
      +   '<input type="range" class="pfm-bb-range" id="' + i.dist + '" min="0" max="150" step="1" aria-label="Shadow distance">'
      +   '<label class="pfm-xf-lab" for="' + i.angle + '">Angle</label>'
      +   '<input type="range" class="pfm-bb-range" id="' + i.angle + '" min="0" max="359" step="1" aria-label="Shadow angle (0 = down)">'
      + '</div>'
      + '<div class="pfm-xf-row">'
      +   '<label class="pfm-xf-lab" for="' + i.blur + '">Blur</label>'
      +   '<input type="range" class="pfm-bb-range" id="' + i.blur + '" min="0" max="240" step="1" aria-label="Shadow blur">'
      + '</div>';
  }
  /* 控件 → override（绝对值的 Shadow 参数）*/
  function shUiRead(pf) {
    var i = shUiIds(pf);
    var co = q(i.color), al = q(i.alpha), di = q(i.dist), an = q(i.angle), bl = q(i.blur);
    var r = di ? (parseInt(di.value, 10) || 0) : 0;
    var deg = an ? (parseInt(an.value, 10) || 0) : 0;
    var rad = deg * Math.PI / 180;
    return {
      color: shRgba(co ? co.value : '#000000', al ? parseInt(al.value, 10) : 35),
      blur: bl ? parseInt(bl.value, 10) : 0,
      offsetX: Math.round(r * Math.sin(rad)),
      offsetY: Math.round(r * Math.cos(rad))
    };
  }
  /* 影子对象 → 控件（长度/角度反算；正在拖的那个控件不覆盖，免得跟用户的手打架）*/
  function shUiSync(pf, sh) {
    if (!sh) return;
    var i = shUiIds(pf);
    var ox = sh.offsetX || 0, oy = sh.offsetY || 0;
    var r = Math.round(Math.sqrt(ox * ox + oy * oy));
    var deg = Math.round(Math.atan2(ox, oy) * 180 / Math.PI);
    if (deg < 0) deg += 360;
    var c = shColorParts(sh.color);
    var v = { color: c.hex, alpha: String(c.alpha), dist: String(Math.min(150, r)),
              angle: String(deg), blur: String(Math.min(240, Math.round(sh.blur || 0))) };
    ['color', 'alpha', 'dist', 'angle', 'blur'].forEach(function (k) {
      var el = q(i[k]); if (!el) return;
      if (document.activeElement === el) return;
      el.value = v[k];
    });
  }
  function shUiBind(pf, root, onInput) {
    var i = shUiIds(pf);
    ['color', 'alpha', 'dist', 'angle', 'blur'].forEach(function (k) {
      var el = q(i[k]); if (!el || el.__shUiB) return;
      el.__shUiB = true;
      /* 2026-10-25：**rAF 帧内合并** —— 一次触摸移动会触发多次 input；原实现每次 input 都重算阴影 +
         重绘 + 存历史 + 刷面板，单帧成本被放大数倍（手机卡顿主因之一）。
         改为每帧最多跑一次（按最后一个值），松手（change）时立即补一次保证终态落地。 */
      var _raf = 0;
      el.addEventListener('input', function () {
        if (_raf) return;
        _raf = requestAnimationFrame(function () {
          _raf = 0;
          try { onInput(); } catch (err) { }
        });
      });
      el.addEventListener('change', function () {
        if (_raf) { try { cancelAnimationFrame(_raf); } catch (e) { } _raf = 0; }
        try { onInput(); } catch (err) { }
      });
    });
  }
  window.__pfmShadowUI = { ids: shUiIds, html: shUiHtml, read: shUiRead, sync: shUiSync, bind: shUiBind };

  function render() {
    if (!FROOT) return;
    var x = loadX();
    FROOT.innerHTML = ''
      + '<div class="pfm-row">'
      +   '<label class="pfm-label">Type</label>'
      +   '<div class="pfm-tx-seg">'
      +     '<button class="pfm-tx-chip" type="button" data-mode="single">Single line</button>'
      +     '<button class="pfm-tx-chip" type="button" data-mode="wrap">Paragraph</button>'
      +   '</div>'
      + '</div>'
      + '<div class="pfm-row" id="pfmTxCurveRow">'
      +   '<label class="pfm-label" for="pfmTxCurve">Curve</label>'
      +   '<input type="range" class="pfm-tx-range" id="pfmTxCurve" min="0" max="360" step="4" value="' + Math.round(x.curveDeg) + '">'
      +   '<span class="pfm-tx-val" id="pfmTxCurveVal">' + Math.round(x.curveDeg) + '°</span>'
      + '</div>'
      + '<div class="pfm-row">'
      +   '<label class="pfm-label" for="pfmTxSpacing">Spacing</label>'
      +   '<input type="range" class="pfm-tx-range" id="pfmTxSpacing" min="-50" max="400" step="5" value="' + x.spacing + '">'
      +   '<span class="pfm-tx-val" id="pfmTxSpacingVal">' + x.spacing + '</span>'
      + '</div>'
      + '<div class="pfm-row" id="pfmTxLineRow">'
      +   '<label class="pfm-label" for="pfmTxLine">Line</label>'
      +   '<input type="range" class="pfm-tx-range" id="pfmTxLine" min="80" max="300" step="5" value="' + Math.round(x.line * 100) + '">'
      +   '<span class="pfm-tx-val" id="pfmTxLineVal">' + x.line.toFixed(2) + '</span>'
      + '</div>'
      + '<div class="pfm-row" id="pfmTxAlignRow">'
      +   '<label class="pfm-label">Align</label>'
      +   '<div class="pfm-tx-al">'
      +     '<button class="pfm-tx-abtn" type="button" data-align="left" title="Align left" aria-label="Align left">' + alignSvg('left') + '</button>'
      +     '<button class="pfm-tx-abtn" type="button" data-align="center" title="Align center" aria-label="Align center">' + alignSvg('center') + '</button>'
      +     '<button class="pfm-tx-abtn" type="button" data-align="right" title="Align right" aria-label="Align right">' + alignSvg('right') + '</button>'
      +   '</div>'
      + '</div>'
      + '<div class="pfm-row">'
      +   '<label class="pfm-label">Shadow</label>'
      +   '<div class="pfm-tx-seg pfm-sh-seg">'
      +     SHADOW_KINDS.map(function (k) {
              return '<button class="pfm-tx-chip" type="button" data-shadow="' + k + '">' + (SHADOW_LABELS[k] || k) + '</button>';
            }).join('')
      +   '</div>'
      + '</div>'
      /* 2026-09-20: 手调参数行（与对象组 Shadow 面板同一份实现，前缀 TxSh） */
      + '<div class="pfm-sh-params" id="pfmTxShParams" style="display:none">'
      +   (window.__pfmShadowUI ? window.__pfmShadowUI.html('TxSh') : '')
      + '</div>';

    FROOT.querySelectorAll('[data-mode]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var wantWrap = (btn.dataset.mode === 'wrap');
        var x2 = loadX(); x2.wrap = wantWrap; saveX(x2);
        var t = selText();
        if (t) {
          var nt = convert(t, wantWrap);
          if (nt) {
            try { nt.set({ charSpacing: x2.spacing, lineHeight: x2.line, textAlign: x2.align }); } catch (e) { }
            /* 互转会新建对象 → 阴影必须跟着搬过去（否则切一次就丢） */
            try { nt.set({ shadow: shadowForX(nt, x2) }); } catch (e) { }
            /* 2026-10-24（多选阴影）：互转新建对象 ⇒ 多选档 + 渲染包装一起搬过去（否则切一次就丢） */
            try { nt._pfmShadows = (t._pfmShadows && t._pfmShadows.length) ? t._pfmShadows.slice() : []; installMultiShadow(nt); } catch (e) { }
          }
          try { if (D && D.save) D.save(); } catch (e) { }
        }
        syncText();
      });
    });

    var sp = q('pfmTxSpacing');
    if (sp) sp.addEventListener('input', function () {
      var x2 = loadX(); x2.spacing = parseInt(sp.value, 10) || 0; saveX(x2);
      var v = q('pfmTxSpacingVal'); if (v) v.textContent = x2.spacing;
      applyExtras();
    });

    var ln = q('pfmTxLine');
    if (ln) ln.addEventListener('input', function () {
      var val = (parseInt(ln.value, 10) || 116) / 100;
      var x2 = loadX(); x2.line = val; saveX(x2);
      var v = q('pfmTxLineVal'); if (v) v.textContent = val.toFixed(2);
      applyExtras();
    });

    var cvu = q('pfmTxCurve');
    if (cvu) cvu.addEventListener('input', function () {
      var dg = parseInt(cvu.value, 10); if (!isFinite(dg)) dg = CURVE_DEF;
      var x3 = loadX(); x3.curveDeg = dg; saveX(x3);
      var v3 = q('pfmTxCurveVal'); if (v3) v3.textContent = dg + '°';
      applyCurve(dg);
    });

    FROOT.querySelectorAll('[data-align]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var x2 = loadX(); x2.align = btn.dataset.align; saveX(x2);
        applyExtras();
        syncText();
      });
    });

    FROOT.querySelectorAll('[data-shadow]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var k = btn.dataset.shadow;
        var x2 = loadX();
        /* 2026-10-24（多选阴影）：None 清空；其余档位 toggle（加减）。shadow 单值 = 最后一个（兼容旧逻辑）。 */
        if (k === 'none') {
          x2.shadows = []; x2.shadow = 'none'; x2.shadowOverride = null;
        } else {
          var arr = x2.shadows || [];
          var idx = arr.indexOf(k);
          if (idx >= 0) arr.splice(idx, 1); else arr.push(k);
          x2.shadows = arr;
          x2.shadow = arr.length ? arr[arr.length - 1] : 'none';
          x2.shadowOverride = null;   /* 换档 = 放弃手调 */
        }
        saveX(x2);
        applyExtras();
        syncText();
      });
    });
    /* 手调参数行：与档位胶囊同一处理，改动即写 override 并套到选中文字 */
    if (window.__pfmShadowUI) window.__pfmShadowUI.bind('TxSh', FROOT, applyTextShadow);

    syncText();
  }

  function sv(id, v) {
    var el = q(id);
    if (!el || document.activeElement === el) return;   // 正在拖动滑块：不回填
    el.value = v;
  }

  function syncText() {
    if (!FROOT) return;
    var t = selText();
    var x = loadX();
    var wrap = t ? (t.type === 'textbox') : x.wrap;

    FROOT.querySelectorAll('[data-mode]').forEach(function (b) {
      b.classList.toggle('on', (b.dataset.mode === 'wrap') === !!wrap);
    });
    FROOT.querySelectorAll('[data-align]').forEach(function (b) {
      b.classList.toggle('on', b.dataset.align === x.align);
    });

    /* 阴影高亮：选中文字对象时反映**该对象实际**的阴影（多选=数组），无选中时反映上次选定档 */
    var _arr = [];
    if (t && t._pfmShadows && t._pfmShadows.length) {
      _arr = t._pfmShadows;
    } else if (t && t.shadow) {
      var _k0 = shadowKind(t.shadow, t);
      if (_k0 && _k0 !== 'none') _arr = [_k0];
    } else if (x.shadows && x.shadows.length) {
      _arr = x.shadows;
    }
    var _set = {};
    for (var _i = 0; _i < _arr.length; _i++) _set[_arr[_i]] = 1;
    FROOT.querySelectorAll('[data-shadow]').forEach(function (b) {
      b.classList.toggle('on', !!_set[b.dataset.shadow]);
    });
    /* 2026-09-20: 手调行 —— 选中文字且有阴影时显示，并把**实际**阴影回填到控件 */
    var _shBox = q('pfmTxShParams');
    var _hasSh = !!(t && t.shadow && window.__pfmShadowUI);
    if (_shBox) _shBox.style.display = _hasSh ? 'block' : 'none';
    if (_hasSh) window.__pfmShadowUI.sync('TxSh', t.shadow);

    /* 2026-10-14: 选中文字的内容回填文本框（框内正在输入时不覆盖，防打断打字） */
    var ti = q('pfmTextInput');
    if (ti && t && document.activeElement !== ti && ti.value !== (t.text || '')) ti.value = t.text || '';

    if (t) {
      var sp = (typeof t.charSpacing === 'number' && isFinite(t.charSpacing)) ? t.charSpacing : 0;
      var ln = (typeof t.lineHeight === 'number' && t.lineHeight > 0) ? t.lineHeight : DEFAULT_LINE;
      var al = t.textAlign || 'center';
      sv('pfmTxSpacing', sp);
      var sv1 = q('pfmTxSpacingVal'); if (sv1) sv1.textContent = Math.round(sp);
      sv('pfmTxLine', Math.round(ln * 100));
      var sv2 = q('pfmTxLineVal'); if (sv2) sv2.textContent = ln.toFixed(2);
      var cu = (typeof t.pfmArcDeg === 'number' && isFinite(t.pfmArcDeg)) ? Math.round(t.pfmArcDeg) : Math.round(x.curveDeg);
      sv('pfmTxCurve', cu);
      var sv3 = q('pfmTxCurveVal'); if (sv3) sv3.textContent = cu + '°';
      FROOT.querySelectorAll('[data-align]').forEach(function (b) {
        b.classList.toggle('on', b.dataset.align === al);
      });
    }
    refreshDisabled();
  }

  /* ---------------------------------------------------------------------------
     「文字沿路径」入口面板（固定属性区，选中形状时出现）
     --------------------------------------------------------------------------- */
  function renderPath() {
    if (!POROOT) return;
    if (POROOT.dataset.built === '1') return;   // 内容只建一次（幂等）
    POROOT.dataset.built = '1';
    POROOT.innerHTML = ''
      + '<button class="pfm-path-btn" type="button" id="pfmPathMake">Text on path</button>'
      + '<div class="pfm-path-hint" id="pfmPathHint"></div>';
    var btn = q('pfmPathMake');
    if (btn) btn.addEventListener('click', createOnPath);
  }

  function syncPath() {
    if (!POROOT) return;
    var sec = q('pfmPathSection');
    var shape = selShape();
    if (sec) sec.style.display = shape ? '' : 'none';
    if (!shape) return;
    var hint = q('pfmPathHint');
    if (hint) hint.textContent = shapeLabel(shape) + ' selected';
  }

  /* ---------------------------------------------------------------------------
     事件绑定 —— 可补绑（见文件头注释）
     --------------------------------------------------------------------------- */
  function syncSoon() {
    try { setTimeout(function () { try { syncText(); syncPath(); } catch (e) { } }, 0); } catch (e) { }
  }

  function bindCanvasEvents() {
    if (bound) return;
    var c = CV();
    if (!c || !c.on) return;
    bound = true;
    try {
      c.on('selection:created', syncSoon);
      c.on('selection:updated', syncSoon);
      c.on('selection:cleared', syncSoon);
      /* 2026-10-14: 画布上双击直接改字 ⇒ 文本框跟着更新 */
      c.on('text:changed', syncSoon);
    } catch (e) { bound = false; }
  }

  function init(deps) {
    if (ready) {
      bindCanvasEvents();
      try { syncText(); syncPath(); } catch (e) { }
      return;
    }
    ready = true;
    D = deps || {};
    FROOT = q('pfmTextExt');
    POROOT = q('pfmPathPanel');
    /* 两个 root 各自判空 —— 路径入口在固定属性区，即使 Text 面板不存在也应工作 */
    if (FROOT) render();
    if (POROOT) renderPath();
    bindInput();
    bindArcWatch();
    bindCanvasEvents();
  }

  window.__hdText = {
    init: init,
    build: build,
    syncText: syncText,
    applyExtras: applyExtras,
    convert: convert,
    arcPath: arcPath,
    curveDeg: curveDeg,
    isText: isText,
    loadX: loadX,
    /* 批次4d 文字沿任意路径 */
    createOnPath: createOnPath,
    syncPath: syncPath,
    selShape: selShape,
    shiftPathD: shiftPathD
  };

  /* defer 脚本执行晚于内联主脚本 → 主动拉取一次依赖注入 */
  try { if (window.__pfmTextBoot) window.__pfmTextBoot(); } catch (e) { }
})();
